import { saveOnboardingCompleted } from "@/api/saveOnboardingCompleted";

const PENDING_KEY = "cardgenius.onboarding.pending";
const COMPLETED_PREFIX = "cardgenius.onboarding.completed.";

export type OnboardingResolveInput = {
  userId?: string | null;
  /** Значение AccountProfile.onboardingCompletedAt с GET /account/profile. */
  onboardingCompletedAt?: string | null;
  /** sessionStorage: сразу после регистрации в этом браузере (optimistic, до ответа бэка). */
  registrationPending?: boolean;
  /**
   * Защита старых юзеров: если уже есть товары — считаем онбординг пройденным,
   * даже если в БД null. Не мешает новым юзерам — у них товаров нет.
   */
  legacyCompletedHint?: boolean;
};

export function markOnboardingPending(): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(PENDING_KEY, "1");
}

export function clearOnboardingPending(): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(PENDING_KEY);
}

export function isOnboardingPending(): boolean {
  if (typeof window === "undefined") return false;
  return window.sessionStorage.getItem(PENDING_KEY) === "1";
}

function completedStorageKey(userId: string): string {
  return `${COMPLETED_PREFIX}${userId}`;
}

export function isOnboardingCompletedLocally(userId: string): boolean {
  if (typeof window === "undefined" || !userId) return false;
  return window.localStorage.getItem(completedStorageKey(userId)) === "1";
}

function markOnboardingCompletedLocally(userId: string): void {
  if (typeof window === "undefined" || !userId) return;
  window.localStorage.setItem(completedStorageKey(userId), "1");
}

export function isOnboardingCompleted(input: OnboardingResolveInput): boolean {
  if (input.onboardingCompletedAt) return true;
  if (input.legacyCompletedHint) return true;
  return false;
}

export function shouldShowOnboarding(input: OnboardingResolveInput): boolean {
  if (isOnboardingCompleted(input)) return false;
  // Optimistic gate сразу после регистрации, пока не успел подгрузиться profile.
  if (input.registrationPending) return true;
  // Иначе доверяем бэку: null в колонке = онбординг не пройден.
  return input.onboardingCompletedAt === null;
}

export async function completeOnboarding(userId: string | null | undefined): Promise<string | null> {
  clearOnboardingPending();
  if (userId) markOnboardingCompletedLocally(userId);

  try {
    return await saveOnboardingCompleted();
  } catch (error) {
    console.error("[onboarding] Failed to sync completion to backend", error);
    // Optimistic fallback — UI не зависнет, при следующем логине бэк всё равно проверит.
    return new Date().toISOString();
  }
}

export function buildOnboardingResolveInput(options: {
  userId?: string | null;
  onboardingCompletedAt?: string | null;
  registrationPending?: boolean;
  goodsCount?: number;
}): OnboardingResolveInput {
  const userId = options.userId ?? null;
  return {
    userId,
    onboardingCompletedAt: options.onboardingCompletedAt ?? null,
    registrationPending: options.registrationPending ?? isOnboardingPending(),
    legacyCompletedHint: Boolean(
      (userId && isOnboardingCompletedLocally(userId)) || (options.goodsCount ?? 0) > 0,
    ),
  };
}
